import React, { Fragment, useRef, useState, useEffect } from "react";
import Select from "react-select";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Overlay } from "react-bootstrap";
import { querySelectorAllDeep } from "query-selector-shadow-dom";
import "./InteractiveMap.css";
import { treeData } from "../mapServices";
import fidlIcon from "assets/img/fidl.png";

// Esri components
import Basemap from "@arcgis/core/Basemap";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import Expand from "@arcgis/core/widgets/Expand";
import Extent from "@arcgis/core/geometry/Extent";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import LayerList from "@arcgis/core/widgets/LayerList";
import Legend from "@arcgis/core/widgets/Legend";
import Map from "@arcgis/core/Map";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import MapView from "@arcgis/core/views/MapView";
import Print from "@arcgis/core/widgets/Print";
import Query from "@arcgis/core/rest/support/Query";
import Search from "@arcgis/core/widgets/Search";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import esriConfig from "@arcgis/core/config";
import "@arcgis/core/assets/esri/themes/light/main.css";

import {
  getAllstates,
  getMajorPestInfo,
  getPestSummaryByAcres,
  getPestSummaryByPest,
} from "../../services/pestTableService";
import {
  getYears,
  getHostsByState,
  getPestsByStateAndHost,
} from "../../services/mapService";




function resolveAfterWait() {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve("Resolved");
    }, 2000); // 2 seconds
  });
}

function inSine(t) {
  return -Math.cos((t * Math.PI) / 2) + 1;
}




function InteractiveMap(props) {
  const mapDiv = useRef(null);
  const map = useRef(null);
  const view = useRef(null);
  const defaultState = { value: 0, label: "All States", isDisabled: false };
  const defaultTree = {
    label: "ash spp.",
    value:
      "https://apps.fs.usda.gov/fsgisx05/rest/services/Hosts/Ash/MapServer/1",
  };
  const defaultPest = {
    label: "emerald ash borer",
    value: 15087,
    isDisabled: false,
  };
  const [viewState, setViewState] = useState(null);
  const [selectedState, setSelectedState] = useState();
  const [selectedTree, setSelectedTree] = useState();
  const [selectedPest, setSelectedPest] = useState();
  const [year, setYear] = useState();
  const [stateList, setStateList] = useState();
  const [treeList, setTreeList] = useState();
  const [pestList, setPestList] = useState();
  // Year 1 is the most recent year available
  const [pestTableListYear1, setPestTableListYear1] = useState();
  const [pestTableListYear2, setPestTableListYear2] = useState();
  const [pestTableListYear3, setPestTableListYear3] = useState();
  const [pestTableListYear4, setPestTableListYear4] = useState();
  const [pestTableListYear5, setPestTableListYear5] = useState();
  const [play, setPlay] = useState(false);
  const isPaused = useRef(false);
  const [extentChange, setExtentChange] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [yearList, setYearList] = useState();
  const [pestPhotoURL, setPestPhotoURL] = useState();
  const [pestPhotoCredit, setPestPhotoCredit] = useState();
  const [pestFIDLURL, setPestFIDLURL] = useState();
  const [pestScientificName, setPestScientificName] = useState();
  const [pestNarrative, setPestNarrative] = useState();
  const [pestTableLink, setPestTableLink] = useState();
  const [pestLoading, setPestLoading] = useState(true);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treePhotoURL, setTreePhotoURL] = useState();
  const [treePhotoCredit, setTreePhotoCredit] = useState();
  const [treeScientificName, setTreeScientificName] = useState(false);
  const [treeAtlasURL, setTreeAtlasURL] = useState();
  const [plantDatabaseURL, setPlantDatabaseURL] = useState();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [announcement, setAnnouncement] = useState("");
  const yearListArray = useRef();
  const initialMapLoad = useRef(true);
  const initialStateLoad = useRef(true);
  const initialTreeLoad = useRef(true);
  const canvasLoaded = useRef(false);
  const prevSelectedTree = useRef();
  const prevSelectedPest = useRef();
  const urlParamsValidated = useRef(false);
  const mapExtentChange = useRef();
  const idsPointsLayerView = useRef(null);
  const statesLayerView = useRef(null);
  const hostLayerView = useRef(null);
  const defaultYear = useRef({
    value: 0,
    label: "2019—2023",
  });
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const shareTarget = useRef(null);
  const navigate = useNavigate();
  esriConfig.apiKey = process.env.REACT_APP_ESRI_API_KEY;
  const esriPublisherID = "gGHDlz6USftL5Pau";
  const esriLayerName = "FPC_Centroids_2019_2023";
  const searchWidget = useRef(null);
  const sidebarID = useRef("map-sidebar");
  const legendID = useRef("legendDiv");
  const shareButtonID = useRef("share-button");
  const yearSwitcherID = useRef("year-switcher");
  const defaultExtent = new Extent({
    xmin: -14249500,
    ymin: 2595288,
    xmax: -7259124,
    ymax: 6741997,
    spatialReference: new SpatialReference({ wkid: 3857 }),
  });
  const alaskaExtent = new Extent({
    xmin: -20150605,
    ymin: 6504738,
    xmax: -14265544,
    ymax: 11676021,
    spatialReference: new SpatialReference({ wkid: 3857 }),
  });
  const dragStartX = useRef();
  const dragStartY = useRef();
  const dragStopX = useRef();
  const dragStopY = useRef();
  const dragThreshold = 400;
  // Map sidebar settings
  const defaultPadding = useRef(50);
  const sidebarWidth = useRef(400);
  const sidebarMargin = useRef(
    -(sidebarWidth.current - defaultPadding.current)
  );

  const toggleSidebar = () => setSidebarExpanded((value) => !value);

  let pointRenderer = new SimpleRenderer({
    symbol: {
      type: "simple-marker",
      color: "red",
      outline: {
        width: 0.5,
        color: "darkred",
      },
    },
    visualVariables: [
      {
        type: "size",
        field: "ACRES",
        minDataValue: 50,
        maxDataValue: 20000,
        minSize: {
          type: "size",
          valueExpression: "$view.scale",
          stops: [
            { value: 1128, size: 4 },
            { value: 9244648, size: 3 },
            { value: 73957191, size: 2 },
            { value: 591657528, size: 1 }, // smallest marker will be 1px at 1:591657528 scale
          ],
        },
        maxSize: {
          type: "size",
          valueExpression: "$view.scale",
          stops: [
            { value: 1128, size: 24 },
            { value: 9244648, size: 18 },
            { value: 73957191, size: 12 },
            { value: 591657528, size: 6 },
          ],
        },
      },
    ],
  });

  const popupIDSTemplate = {
    title: "Damage Location",
    overwriteActions: true,
    fieldInfos: [
      {
        fieldName: "ACRES",
        format: {
          digitSeparator: true,
          places: 0,
          round: true,
        },
      },
    ],
    content:
      "<b>Pest Species:</b> {DCA_COMMON}<br><b>Tree Species/Group:</b> {Host_Categ}<br><b>Survey Year:</b> {SURVEY_YEA}<br><b>Acres:</b> {ACRES}",
  };

  const statesLayer = new FeatureLayer({
    url: "https://apps.fs.usda.gov/arcx/rest/services/EDW/EDW_GeopoliticalUnit_01/MapServer/0",
    id: "states-layer",
    outFields: ["NAME"],
    title: "U.S. States",
    opacity: 0.3,
  });

  const idsPointsLayer = new FeatureLayer({
    //url: "https://services1.arcgis.com/gGHDlz6USftL5Pau/arcgis/rest/services/FPC_Centroids_2019_2023/FeatureServer",
    url:
      "https://services1.arcgis.com/" +
      esriPublisherID +
      "/arcgis/rest/services/" +
      esriLayerName +
      "/FeatureServer",
    minScale: 100000000,
    maxScale: 10,
    id: "pest-layer",
    outFields: [
      "SURVEY_YEA",
      "HOST",
      "HOST_CODE",
      "DCA_COMMON",
      "DCA_CODE",
      "DAMAGE_TYP",
      "ACRES",
      "Buffer",
      "Host_Categ",
      "STATE",
    ],
    title: "Damage Locations",
    popupTemplate: popupIDSTemplate,
    renderer: pointRenderer,
    sizeOptimizationEnabled: true,
  });

  const filterIDSRecords = (year, pest, tree) => {
    let filterArray = [];
    if (year.value !== 0) {
      filterArray.push(`SURVEY_YEA = ${year.value}`);
    }
    if (pest && pest.label !== "All Pests") {
      filterArray.push(`DCA_CODE = ${pest.value}`);
    }
    if (tree.label !== "All Trees") {
      filterArray.push(`Host_Categ = '${tree.label}'`);
    }
    const filter = filterArray.join(" AND ");
    return filter;
  };

  // If selected state changes
  useEffect(() => {
    // If a state is selected, watch for the user panning away from it
    if (view.current && selectedState) {
      mapExtentChange.current = view.current.on("drag", mapExtentChangeHandler);
      if (selectedState.label === "All States") {
        if (!initialStateLoad.current && view.current && defaultExtent) {
          view.current.goTo(defaultExtent);
        }
      } else if (selectedState.label === "Alaska") {
        view.current.goTo(alaskaExtent);
      } else {
        const query = new Query();
        query.where = `NAME = '${selectedState.label}'`;
        query.returnGeometry = true;
        statesLayer.queryFeatures(query).then(function (results) {
          const features = results.features;
          if (features) {
            view.current.goTo(features);
          }
        });
      }
    }
    if (urlParamsValidated.current && stateList) {
      if (initialStateLoad.current) {
        // First time state is set from default or URL parameters
        initialStateLoad.current = false;
      }
      else if (selectedState) {
        prevSelectedTree.current = selectedTree;
        prevSelectedPest.current = selectedPest;
        setExtentChange(false);
        getHostsByState(
          esriPublisherID,
          esriLayerName,
          selectedState.label
        ).then((result) => {
          const treeOptions = result.data.features;
          const cleanedTreeList = cleanTreeList(treeOptions);
          setTreeList(cleanedTreeList);
          const validTreeSelection = cleanedTreeList.find(  // Keep tree and pest selection if they are valid with new state selection
            (element) => element.label === prevSelectedTree.current.label
          );
          if (validTreeSelection) {

            getPestsByStateAndHost(esriPublisherID, esriLayerName, selectedState.label, prevSelectedTree.current.label)
              .then((result) => {  // Populate pest options associated with selected tree
                const pestOptions = result.data.features;
                const cleanedPestList = cleanPestList(pestOptions);
                setPestList(cleanedPestList);
                if (prevSelectedPest.current) { // If previously selected pest is available, keep selection
                  const validPestSelection = cleanedPestList.find(
                    (element) =>
                      element.label === prevSelectedPest.current.label
                  );
                  if (validPestSelection) {
                    setSelectedPest(prevSelectedPest.current);
                  } else {
                    setSelectedPest("");
                    setYear(defaultYear.current);
                  }
                } else {
                  setSelectedPest("");
                  setYear(defaultYear.current);
                }
              });
          }
          else {
            setSelectedTree("");
            setSelectedPest("");
            removeTreeLayer();
            setYear(defaultYear.current);
          }
        });
      }
      else {
        setSelectedState(defaultState);
        setYear(defaultYear.current);
      }
    }
  }, [selectedState]);

  // If selected tree changes
  useEffect(() => {
    if (urlParamsValidated.current && selectedTree) {
      setTreeLoading(true);
      if (
        treeData[selectedTree.label]
        // && treeData[selectedTree.label] !== "All Trees"
      ) {
        setTreeScientificName(treeData[selectedTree.label]?.scientificName);
        setTreePhotoURL(treeData[selectedTree.label]?.photoName);
        setTreePhotoCredit(treeData[selectedTree.label]?.photoCredit);
        setTreeAtlasURL(treeData[selectedTree.label]?.treeAtlasURL);
        setPlantDatabaseURL(treeData[selectedTree.label]?.usdaPlantDbURL);
        setTreeLoading(false);
      } else {
        setTreeScientificName();
        setTreeAtlasURL();
        setPlantDatabaseURL();
        setTreePhotoURL();
        setTreePhotoCredit();
        setTreeLoading(false);
      }
      prevSelectedPest.current = selectedPest;
      // Add tree range layer
      if (mapDiv.current && view.current) {
        removeTreeLayer();
        if (
          selectedTree.label !== "All Trees" &&
          selectedTree.label !== "mixed species"
        ) {
          const lyr = treeData[selectedTree.label]?.layerURL;
          if (lyr) {
            const sublayerId = lyr.split("/").slice(-1)[0];
            const stringLength = lyr.length - sublayerId.length;
            const layerUrl = lyr.substring(0, stringLength);
            const hostLayer = new MapImageLayer({
              url: layerUrl,
              sublayers: [
                { id: sublayerId, visible: true, title: selectedTree.label },
              ],
              id: "host-layer",
              opacity: 0.4,
              title: "Tree Species/Group Range",
            });
            map.current.add(hostLayer);
            map.current.reorder(hostLayer, 0);
            view.current.whenLayerView(hostLayer).then(function (layerView) {
              hostLayerView.current = layerView;
            });
          }
        }
      }
      if (initialTreeLoad.current) {
        // First time state is set from default or URL parameters
        initialTreeLoad.current = false;
      } else {
        // Populate pest options associated with selected tree
        getPestsByStateAndHost(
          esriPublisherID,
          esriLayerName,
          selectedState.label,
          selectedTree.label
        ).then((result) => {
          const pestOptions = result.data.features;
          const cleanedPestList = cleanPestList(pestOptions);
          setPestList(cleanedPestList);
          // Keep pest selection if it is valid with new state selection
          if (prevSelectedPest.current) {
            const validPestSelection = cleanedPestList.find(
              (element) => element.label === prevSelectedPest.current.label
            );
            if (validPestSelection) {
              reloadPestLayer();
            } else {
              setSelectedPest("");
              setYear(defaultYear.current);
            }
          } else {
            setYear(defaultYear.current);
          }
        });
      }
    }
    if (!selectedTree) {
      const allTrees = {
        label: "All Trees",
        value: "",
      };
      setSelectedTree(allTrees);
    }
  }, [selectedTree]);

  // If selected pest changes
  useEffect(() => {
    if (selectedPest && yearListArray.current) {
      if (selectedPest.label === "All Pests") {
        setPestLoading(true);
        setPestPhotoURL("16038-a-WEB.jpg");
        setPestPhotoCredit(
          "Tim Tigner, Virginia Department of Forestry, Bugwood.org"
        );
        setPestScientificName();

        setPestNarrative(
          "This view displays all forest pest species for the selected tree species/group, including abiotic agents like wind and fire."
        );
        setPestTableLink("/pest-information/by-pest");
        setPestFIDLURL();
        setPestLoading(false);
      } else {
        setPestLoading(true);
        const pestPromise1 = new Promise((resolve, reject) => {
          // Check if pest has pest table data available
          // Some pests do not have data for the latest year, so it checks earlier years
          linkToPestTable();
          resolve("pest promise 1");
        });
        const pestPromise2 = new Promise((resolve, reject) => {
          getMajorPestInfo({
            years: yearListArray.current,
            damageTypeId: selectedPest.value,
          }).then(
            (result) => {
              if (result.data.length > 0) {
                let lastItemIndex = result.data.length - 1;
                setPestPhotoURL(result.data[0]?.Photo_Name_Web);
                setPestPhotoCredit(result.data[0]?.Photo_Credit);
                setPestScientificName(result.data[0]?.Scientific_Name);
                setPestNarrative(result.data[lastItemIndex]?.Narrative);
                resolve("pest promise 2");
              } else {
                setPestPhotoURL();
                setPestPhotoCredit();
                setPestScientificName();
                setPestNarrative();
                resolve("pest promise 2");
              }
            },
            (error) => {
              console.error(`getMajorPestInfo error: ${error.message}`);
              setPestPhotoURL();
              setPestPhotoCredit();
              setPestScientificName();
              setPestNarrative();
              resolve("pest promise 2");
            }
          );
        });
        const pestPromise3 = new Promise((resolve, reject) => {
          getPestSummaryByAcres({
            years: yearListArray.current,
            damageTypeId: selectedPest.value,
          }).then(
            (result) => {
              if (result.data.data.length > 0) {
                const fidlUrl = result.data.data[0]?.weblink;
                if (fidlUrl === "") {
                  setPestFIDLURL();
                } else {
                  setPestFIDLURL("https://www.fs.usda.gov" + fidlUrl);
                }
                resolve("pest promise 3");
              } else {
                setPestFIDLURL();
                resolve("pest promise 3");
              }
            },
            (error) => {
              console.error(`getPestSummaryByAcres error: ${error.message}`);
              setPestFIDLURL();
              resolve("pest promise 3");
            }
          );
        });
        // Wait until all pest data are loaded to display it
        Promise.all([pestPromise1, pestPromise2, pestPromise3]).then(
          (values) => {
            setPestLoading(false);
          }
        );
      }
    } else {
      setPestPhotoURL();
      setPestScientificName();
      setPestPhotoCredit();
      setPestNarrative();
      setPestTableLink();
      setPestFIDLURL();
    }
    setYear(defaultYear.current);
    reloadPestLayer();
  }, [selectedPest]);

  // If year filter changes
  useEffect(() => {
    if (
      mapDiv.current &&
      view.current &&
      idsPointsLayerView.current !== undefined &&
      idsPointsLayerView.current != null
    ) {
      view.current.closePopup();
      if (selectedTree && selectedPest) {
        reloadPestLayer();
      }
    }
  }, [year]);

  // Generate list of available pests for each year
  // Then set pest table link for initially loaded pest
  useEffect(() => {
    if (
      selectedPest &&
      selectedPest.label !== "All Pests" &&
      pestTableListYear1 &&
      pestTableListYear2 &&
      pestTableListYear3 &&
      pestTableListYear4 &&
      pestTableListYear5
    ) {
      linkToPestTable();
    }
  }, [
    pestTableListYear1,
    pestTableListYear2,
    pestTableListYear3,
    pestTableListYear4,
    pestTableListYear5,
  ]);

  // Zooms to a state when you click on it
  // But if you click on a point, do not zoom to the state
  useEffect(() => {
    if (stateList && view.current && statesLayerView) {
      view.current.on("click", function (event) {
        const screenPoint = {
          x: event.x,
          y: event.y,
        };
        // Get layer attributes at click point
        view.current.hitTest(screenPoint).then(function (response) {
          if (response.results.length) {
            let stateLayerResult = false;
            let idsLayerResult = false;
            try {
              stateLayerResult = response.results.find(function (result) {
                return result.graphic.attributes.NAME;
              })?.graphic;
              idsLayerResult = response.results.find(function (result) {
                return result.graphic.attributes.SURVEY_YEA;
              })?.graphic;
            } catch { }
            if (stateLayerResult && !idsLayerResult) {
              const foundState = stateList.find(
                (element) => element.label === stateLayerResult.attributes.NAME
              );
              if (foundState) {
                setSelectedState(foundState);
              }
            }
          }
        });
      });
    }
  }, [stateList, statesLayerView]);

  const reloadPestLayer = () => {
    if (mapDiv.current && view.current) {
      view.current.closePopup();
      if (selectedTree && selectedPest) {
        if (idsPointsLayerView.current) {
          idsPointsLayerView.current.filter = {
            where: filterIDSRecords(year, selectedPest, selectedTree),
          };
        } else {
          // Add the layer if it is not already present
          map.current.add(idsPointsLayer);
          view.current.whenLayerView(idsPointsLayer).then(function (layerView) {
            idsPointsLayerView.current = layerView;
            idsPointsLayerView.current.filter = {
              where: filterIDSRecords(year, selectedPest, selectedTree),
            };
          });
        }
      }
      else {
        idsPointsLayerView.current = null;  // Remove point layer if new selection is null
        const pestLyr = map.current.findLayerById("pest-layer");
        if (pestLyr) {
          map.current.remove(pestLyr);
        }
      }
    }
  };

  const linkToPestTable = () => {
    if (
      !selectedPest ||
      !pestTableListYear1 ||
      !pestTableListYear2 ||
      !pestTableListYear3 ||
      !pestTableListYear4 ||
      !pestTableListYear5
    ) {
      setPestTableLink();
      return;
    }

    const pest = selectedPest.label;
    const encodedPest = encodeURIComponent(pest);
    const baseUrl = "/pest-information/by-pest";
    const lastIndex = yearListArray.current.length - 1;

    const years = [
      { pests: pestTableListYear1, url: `${baseUrl}?ab=true&p=${encodedPest}` },
      { pests: pestTableListYear2, url: `${baseUrl}?p=${encodedPest}&yr=${yearListArray.current[lastIndex]}` },
      { pests: pestTableListYear3, url: `${baseUrl}?p=${encodedPest}&yr=${yearListArray.current[lastIndex - 1]}` },
      { pests: pestTableListYear4, url: `${baseUrl}?p=${encodedPest}&yr=${yearListArray.current[lastIndex - 2]}` },
      { pests: pestTableListYear5, url: `${baseUrl}?p=${encodedPest}&yr=${yearListArray.current[lastIndex - 3]}` },
    ];

    const match = years.find(({ pests }) => pests.includes(pest));
    setPestTableLink(match?.url);
  };

  const removeTreeLayer = () => {
    if (mapDiv.current && view.current && hostLayerView.current) {
      const hostLyr = map.current.findLayerById("host-layer");
      hostLayerView.current = null;
      if (hostLyr) {
        map.current.remove(hostLyr);
      }
    }
  };

  const setSidebarWidth = () => {
    const btnExpand = document.getElementById("btnExpand");
    const sidebar = document.getElementById("map-sidebar");
    if (window.innerWidth < 821) {
      // Mobile
      sidebarWidth.current = window.innerWidth;
      sidebarMargin.current = -(sidebarWidth.current - defaultPadding.current);
    } else {
      // Not mobile
      sidebarWidth.current = 400;
      sidebarMargin.current = -(sidebarWidth.current - defaultPadding.current);
    }
    if (btnExpand?.attributes.expanded.value === "true") {
      sidebar.style.marginLeft = `0px`;
    } else if (btnExpand?.attributes.expanded.value === "false") {
      sidebar.style.marginLeft = `${sidebarMargin.current}px`;
    }
  };

  // Initial page load
  useEffect(() => {
    setAnnouncement("You are now on the Interactive Map page");
    const btnExpand = document.getElementById("btnExpand");
    if (
      window.innerWidth < 821 &&
      btnExpand?.attributes.expanded.value === "true"
    ) {
      sidebarWidth.current = window.innerWidth;
      sidebarMargin.current = -(window.innerWidth - defaultPadding.current);
      setSidebarExpanded(false);
      collapse();
    }
    window.addEventListener("resize", setSidebarWidth);
    // Get year range from GIS data
    getYears(esriPublisherID, esriLayerName).then((result) => {
      const rawYears = result.data.features;
      const allYears = [];
      const allYearsArray = [];
      const yearOptionList = [];
      rawYears.map((x) => {
        const yr = x.attributes.SURVEY_YEA;
        allYears.push(yr);
        yearOptionList.push({ value: yr, label: yr.toString() });
        allYearsArray.push(yr);
        return allYearsArray;
      });
      allYears.sort();
      allYearsArray.sort();
      // Add All Years option
      const yearRange = {
        value: 0,
        label: allYears[0] + "—" + allYears[4],
      };
      yearOptionList.unshift(yearRange);
      setYearList(yearOptionList);
      yearListArray.current = allYearsArray;
      defaultYear.current = yearOptionList[0];
      // Gets a list of pests with available Pest Table data
      getPestSummaryByPest({
        damageTypeId: 93,
        years: [allYearsArray[4]],
      }).then(
        (result) => {
          const pestList = [];
          result.data.data.forEach((element) => {
            if (!pestList.includes(element.CommonName)) {
              pestList.push(element.CommonName);
            }
          });
          setPestTableListYear1(pestList);
        },
        (error) => {
          console.error(`getPestSummaryByPest error: ${error.message}`);
        }
      );
      getPestSummaryByPest({
        damageTypeId: 93,
        years: [allYearsArray[3]],
      }).then(
        (result) => {
          const pestList = [];
          result.data.data.forEach((element) => {
            if (!pestList.includes(element.CommonName)) {
              pestList.push(element.CommonName);
            }
          });
          setPestTableListYear2(pestList);
        },
        (error) => {
          console.error(`getPestSummaryByPest error: ${error.message}`);
        }
      );
      getPestSummaryByPest({
        damageTypeId: 93,
        years: [allYearsArray[2]],
      }).then(
        (result) => {
          const pestList = [];
          result.data.data.forEach((element) => {
            if (!pestList.includes(element.CommonName)) {
              pestList.push(element.CommonName);
            }
          });
          setPestTableListYear3(pestList);
        },
        (error) => {
          console.error(`getPestSummaryByPest error: ${error.message}`);
        }
      );
      getPestSummaryByPest({
        damageTypeId: 93,
        years: [allYearsArray[1]],
      }).then(
        (result) => {
          const pestList = [];
          result.data.data.forEach((element) => {
            if (!pestList.includes(element.CommonName)) {
              pestList.push(element.CommonName);
            }
          });
          setPestTableListYear4(pestList);
        },
        (error) => {
          console.error(`getPestSummaryByPest error: ${error.message}`);
        }
      );
      getPestSummaryByPest({
        damageTypeId: 93,
        years: [allYearsArray[0]],
      }).then(
        (result) => {
          const pestList = [];
          result.data.data.forEach((element) => {
            if (!pestList.includes(element.CommonName)) {
              pestList.push(element.CommonName);
            }
          });
          setPestTableListYear5(pestList);
        },
        (error) => {
          console.error(`getPestSummaryByPest error: ${error.message}`);
        }
      );
    });
    getAllstates().then((result) => {
      const allStates = { value: 0, label: "All States", isDisabled: false };
      const stateOptions = result.data;
      stateOptions.unshift(allStates);
      const listOfStates = Array.from(stateOptions);
      setStateList(listOfStates);
      setYear(defaultYear.current);
      // Validate URL parameters (if any)
      const urlState = searchParams.get("state");
      const urlTree = searchParams.get("tree");
      const urlPest = searchParams.get("pest");
      const urlParametersSpecified = urlState || urlTree | urlPest;
      // Validate state URL parameter
      if (urlState) {
        const foundState = listOfStates.find(
          (element) => element.label === urlState
        );
        if (foundState) {
          setSelectedState(foundState);
          getHostsByState(
            esriPublisherID,
            esriLayerName,
            foundState.label
          ).then((result) => {
            const treeOptions = result.data.features;
            const cleanedTreeList = cleanTreeList(treeOptions);
            setTreeList(cleanedTreeList);
            // Validate tree URL parameter
            if (urlTree) {
              // If tree URL parameter is "pine" instead of "pine spp."
              const repairTreeParam = urlTree + " spp.";
              let foundTree = cleanedTreeList.find((obj) => {
                return obj.label === urlTree;
              });
              if (!foundTree) {
                foundTree = cleanedTreeList.find((obj) => {
                  return obj.label === repairTreeParam;
                });
              }
              if (foundTree) {
                setSelectedTree(foundTree);
                getPestsByStateAndHost(
                  esriPublisherID,
                  esriLayerName,
                  foundState.label,
                  foundTree.label
                ).then((result) => {
                  const cleanedPestList = cleanPestList(result.data.features);
                  setPestList(cleanedPestList);
                  // Validate pest URL parameter
                  if (urlPest) {
                    let foundPestIndex = -1;
                    for (let i = 0; i < cleanedPestList.length; i++) {
                      if (cleanedPestList[i].label === urlPest) {
                        foundPestIndex = i;
                        setSelectedPest(cleanedPestList[i]);
                      }
                    }
                    if (foundPestIndex === -1) {  // If provided pest URL parameter isn't valid, set to first option for the tree
                      setSelectedPest(cleanedPestList[0]);
                    } else {
                      setSelectedPest(cleanedPestList[foundPestIndex]);
                    }
                  } else {
                    // If no pest URL parameter provided, set to first option for the tree
                    setSelectedPest(cleanedPestList[0]);
                  }
                });
              } else {
                // If provided tree URL parameter isn't valid, set to first option for the state
                setSelectedTree(cleanedTreeList[0]);
                getPestsByStateAndHost(
                  esriPublisherID,
                  esriLayerName,
                  foundState.label,
                  cleanedTreeList[0].label
                ).then((result) => {
                  const cleanedPestList = cleanPestList(result.data.features);
                  setPestList(cleanedPestList);
                  setSelectedPest(cleanedPestList[0]);
                });
              }
            } else {
              // If no tree URL parameter, set to first option for the state
              setSelectedTree(cleanedTreeList[0]);
              getPestsByStateAndHost(
                esriPublisherID,
                esriLayerName,
                foundState.label,
                cleanedTreeList[0].label
              ).then((result) => {
                const cleanedPestList = cleanPestList(result.data.features);
                setPestList(cleanedPestList);
                setSelectedPest(cleanedPestList[0]);
              });
            }
          });
        }
      } else {
        // Default options if no URL parameters specified
        setSelectedState(defaultState);
        getHostsByState(
          esriPublisherID,
          esriLayerName,
          defaultState.label
        ).then((treeResult) => {
          const treeOptions = treeResult.data.features;
          const cleanedTreeList = cleanTreeList(treeOptions);
          setTreeList(cleanedTreeList);
          setSelectedTree(defaultTree);
          getPestsByStateAndHost(
            esriPublisherID,
            esriLayerName,
            defaultState.label,
            defaultTree.label
          ).then((pestResult) => {
            const cleanedPestList = cleanPestList(pestResult.data.features);
            setPestList(cleanedPestList);
            setSelectedPest(defaultPest);
          });
        });
      }
      urlParamsValidated.current = true;

      // If any URL parameters are specified (valid or not), redirect the page
      if (urlParametersSpecified) {
        navigate("/map");
      }
    });
  }, []);

  const handleShare = () => {
    setShowShareTooltip(true);
    let url = window.location.href;
    if (url.includes("?")) {
      url = url.substring(0, url.indexOf("?"));
    }
    let urlComponents = [];
    if (selectedState) {
      url += "?";
      urlComponents.push("state=" + encodeURIComponent(selectedState.label));
    }
    if (selectedState && selectedTree) {
      urlComponents.push("tree=" + encodeURIComponent(selectedTree.label));
    }
    if (selectedState && selectedTree && selectedPest) {
      urlComponents.push("pest=" + encodeURIComponent(selectedPest.label));
    }
    url += urlComponents.join("&");
    navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
      if (result.state === "granted") {
        navigator.clipboard.writeText(url);
      }
    });
  };

  const mapExtentChangeHandler = (event) => {
    if (event.action === "start") {
      dragStartX.current = event.x;
      dragStartY.current = event.y;
      dragStopX.current = event.x;
      dragStopY.current = event.y;
    }
    if (event.action === "end") {
      dragStopX.current = event.x;
      dragStopY.current = event.y;
      const netX = Math.abs(dragStartX.current - dragStopX.current);
      const netY = Math.abs(dragStartY.current - dragStopY.current);
      const distanceTraveled = Math.hypot(netX, netY);
      if (distanceTraveled > dragThreshold && view.current.zoom > 4) {
        if (selectedState.label === "All States") {
          searchWidget.current.searchTerm = "";
        } else {
          setExtentChange(true);
        }
      }
      mapExtentChange.current.remove();
    }
  };

  const sleep = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const timeSliderBackward = () => {
    let currentYearIndex = yearList.findIndex(
      (element) => element.value === year.value
    );
    let newYearIndex = currentYearIndex - 1;
    if (currentYearIndex <= 0) {
      newYearIndex = 5;
    }
    setYear(yearList[newYearIndex]);
  };

  const timeSliderPlay = () => {
    let waitTime = 4000;
    setYear(yearList[1]);
    setPlay(true);
    isPaused.current = false;
    sleep(waitTime).then(() => {
      if (!isPaused.current) {
        setYear(yearList[2]);
        sleep(waitTime).then(() => {
          if (!isPaused.current) {
            setYear(yearList[3]);
            sleep(waitTime).then(() => {
              if (!isPaused.current) {
                setYear(yearList[4]);
                sleep(waitTime).then(() => {
                  if (!isPaused.current) {
                    setYear(yearList[5]);
                    isPaused.current = false;
                    setPlay(false);
                  }
                });
              }
            });
          }
        });
      }
    });
  };

  const timeSliderStop = () => {
    setPlay(false);
    isPaused.current = true;
  };

  const timeSliderForward = () => {
    let currentYearIndex = yearList.findIndex(
      (element) => element.value === year.value
    );
    let newYearIndex = (currentYearIndex + 1) % 6;
    setYear(yearList[newYearIndex]);
  };



  // Show copy link tooltip for only a few seconds
  useEffect(() => {
    if (showShareTooltip) {
      resolveAfterWait().then(() => {
        setShowShareTooltip(false);
      });
    }
  }, [showShareTooltip]);

  // Initial map load
  useEffect(() => {
    if (mapDiv.current) {
      map.current = new Map({
        basemap: "arcgis-topographic",
      });
      view.current = new MapView({
        container: mapDiv.current,
        extent: defaultExtent,
        map: map.current,
        popupEnabled: true,
        popup: {
          dockEnabled: false,
          dockOptions: {
            buttonEnabled: false,
          },
        },
        constraints: {
          minZoom: 4,
          maxZoom: 11,
        },
      });
      // Add US States reference layer
      map.current.add(statesLayer);
      view.current.whenLayerView(statesLayer).then(function (layerView) {
        statesLayerView.current = layerView;
        // ACCESSIBILITY FEATURES - Must wait for layer to load
        const esriSearchContainer = document.getElementsByClassName(
          "esri-search__container"
        )[0];
        if (esriSearchContainer) {
          esriSearchContainer.title = "Search for features in the map";
        }
        const esriSearchButton = document.getElementsByClassName(
          "esri-search__submit-button"
        )[0];
        if (esriSearchButton) {
          esriSearchButton.innerHTML = "Search";
        }
        const widgetZoomButtons = querySelectorAllDeep(
          ".esri-zoom .interaction-container button"
        );
        if (widgetZoomButtons) {
          widgetZoomButtons.forEach((element) => {
            element.style.outlineWidth = "0px";
          });
        }
        const widgetExpandButtons = querySelectorAllDeep(
          ".esri-expand .interaction-container button"
        );
        if (widgetExpandButtons) {
          widgetExpandButtons.forEach((element) => {
            element.style.outlineWidth = "0px";
          });
        }
        const zoomButtons = querySelectorAllDeep(".esri-zoom .icon--start");
        if (zoomButtons) {
          zoomButtons.forEach((element) => {
            element.ariaHidden = false;
          });
        }
        const zoomSvgElements = querySelectorAllDeep(".esri-zoom svg.svg");
        if (zoomSvgElements) {
          zoomSvgElements.forEach((element, index) => {
            element.role = "img";
            element.ariaHidden = false;
            if (index === 0) {
              element.ariaLabel = "Zoom In";
            } else if (index === 1) {
              element.ariaLabel = "Zoom Out";
            }
          });
        }
      });
      // ACCESSIBILITY FEATURES - Must wait for canvas to load
      view.current.when().then(() => {
        const canvasElement = document.querySelector("canvas");
        if (canvasElement && !canvasLoaded.current) {
          canvasElement.innerText =
            "A tabular version of the data is available through the Pest Information page";
          canvasElement.role = "img";
          canvasElement.tabIndex = -1;
          canvasElement.ariaLabel = "Interactive map canvas";
          canvasLoaded.current = true;
        }
        const attribution = document.getElementsByClassName(
          "esri-attribution__sources"
        )[0];
        if (attribution) {
          attribution.role = "button";
        }
        const expandButtons = document.querySelectorAll(".esri-collapse__icon");
        if (expandButtons) {
          expandButtons.forEach((element) => {
            element.ariaHidden = false;
          });
        }
        const widgetSvgElements = querySelectorAllDeep(".esri-expand svg.svg");
        if (widgetSvgElements) {
          widgetSvgElements.forEach((element, index) => {
            element.role = "img";
            element.ariaHidden = false;
            if (index === 0) {
              element.ariaLabel = "Expand/Collapse Basemap Gallery";
            } else if (index === 1) {
              element.ariaLabel = "Expand/Collapse Layer List";
            } else if (index === 2) {
              element.ariaLabel = "Expand/Collapse Print Controls";
            }
          });
        }
      });

      // Sidebar collapse/expand functionality
      view.current.padding.left = sidebarWidth.current;
      const btnExpand = document.getElementById("btnExpand");
      btnExpand.addEventListener("click", collapse);
      // Set accordion sections 1, 2 and 4 as closed
      const a1 = document.getElementById("a1");
      a1.hidden = true;
      const a2 = document.getElementById("a2");
      a2.hidden = true;
      const a4 = document.getElementById("a4");
      a4.hidden = true;
      // Add map widgets
      searchWidget.current = new Search({
        view: view.current,
        includeDefaultSources: true,
        popupEnabled: true,
        resultGraphicEnabled: true,
      });
      const basemapGallery = new BasemapGallery({
        view: view.current,
        source: [
          Basemap.fromId("topo-vector"),
          Basemap.fromId("hybrid"),
          Basemap.fromId("streets-relief-vector"),
          Basemap.fromId("arcgis-human-geography"),
        ],
      });
      const basemapExpand = new Expand({
        view: view.current,
        content: basemapGallery,
        expanded: false,
        autoCollapse: true,
        expandTooltip: "Expand Basemap Controls",
        collapseTooltip: "Collapse Basemap Controls",
      });
      const layerList = new LayerList({
        view: view.current,
      });
      const printControls = new Print({
        view: view.current,
        printServiceUrl:
          "https://apps.fs.usda.gov/arcx/rest/services/Utilities/PrintingTools/GPServer/Export%20Web%20Map%20Task",
        allowedLayouts: ["letter-ansi-a-landscape", "letter-ansi-a-portrait"],
        allowedFormats: ["jpg", "png32", "gif", "pdf"],
        templateOptions: {
          author: "U.S Forest Service",
          copyright: "U.S Department of Agriculture",
        },
      });
      const layerListExpand = new Expand({
        view: view.current,
        content: layerList,
        expanded: false,
        autoCollapse: true,
        expandTooltip: "Expand Layer List Controls",
        collapseTooltip: "Collapse Layer List Controls",
        role: "button",
      });
      const printExpand = new Expand({
        view: view.current,
        content: printControls,
        expanded: false,
        autoCollapse: true,
        expandTooltip: "Expand Print Controls",
        collapseTooltip: "Collapse Print Controls",
        role: "button",
      });
      new Legend({
        view: view.current,
        container: legendID.current,
      });
      // One widget panel can be expanded at once, not multiple
      searchWidget.current.watch("suggestions", function (e) {
        if (e) {
          basemapExpand.expanded = false;
          layerListExpand.expanded = false;
          printExpand.expanded = false;
        }
      });
      basemapExpand.watch("expanded", function (e) {
        if (e) {
          layerListExpand.expanded = false;
          printExpand.expanded = false;
        }
      });
      layerListExpand.watch("expanded", function (e) {
        if (e) {
          basemapExpand.expanded = false;
          printExpand.expanded = false;
        }
      });
      printExpand.watch("expanded", function (e) {
        if (e) {
          basemapExpand.expanded = false;
          layerListExpand.expanded = false;
        }
      });
      const closeAllWidgets = () => {
        printExpand.expanded = false;
        basemapExpand.expanded = false;
        layerListExpand.expanded = false;
      };
      const shareBtn = document.getElementById(shareButtonID.current);
      if (shareBtn) {
        shareBtn.onclick = () => closeAllWidgets();
      }
      view.current.ui.add(searchWidget.current, {
        position: "top-right",
      });
      view.current.ui.move("zoom", {
        position: "top-right",
      });
      view.current.ui.add(basemapExpand, {
        position: "top-right",
      });
      view.current.ui.add(layerListExpand, {
        position: "top-right",
      });
      view.current.ui.add(printExpand, {
        position: "top-right",
      });
      view.current.ui.add(document.getElementById(shareButtonID.current), {
        position: "top-right",
      });
      view.current.ui.add(
        document.getElementById(yearSwitcherID.current),
        "top-left"
      );
      initialMapLoad.current = false;
      // ACCESSIBILITY - Manually added title because Esri does not provide it
      const appElement =
        document.getElementsByClassName("esri-view-surface")[0];
      if (appElement) {
        appElement.tabIndex = "-1";
      }
    }
    document.title = "Forest Pest Conditions - Interactive Map";
  }, [mapDiv]);

  // Clear the search term (if there is one) when the user pans the map
  useEffect(() => {
    if (!initialMapLoad.current) {
      searchWidget.current.searchTerm = "";
    }
  }, [extentChange]);

  const cleanTreeList = (input) => {
    const allTrees = {
      label: "All Trees",
      value: "",
    };
    const treeArray = [];
    for (let i = 0; i < input.length; i++) {
      if (
        treeData[input[i].attributes.Host_Categ] ||
        input[i].attributes.Host_Categ === "ohia spp."
      ) {
        const newTreeItem = {
          label: input[i].attributes.Host_Categ,
          value: treeData[input[i].attributes.Host_Categ].layerURL,
          isDisabled: false,
        };
        treeArray.push(newTreeItem);
      }
    }
    treeArray.unshift(allTrees);
    return treeArray;
  };

  const cleanPestList = (input) => {
    const allPests = {
      label: "All Pests",
      value: "",
    };
    const pestArray = [];
    for (let i = 0; i < input.length; i++) {
      const newPestItem = {
        label: input[i].attributes.DCA_COMMON,
        value: input[i].attributes.DCA_CODE,
        isDisabled: false,
      };
      pestArray.push(newPestItem);
    }
    pestArray.unshift(allPests);
    return pestArray;
  };

  const cleanPestName = (string) => {
    if (string) {
      // Remove anything in parentheses to keep the text short
      const parenthesisIndex = string.indexOf("(");
      if (parenthesisIndex > 0) {
        const newString = string.substring(0, parenthesisIndex - 1);
        return newString.charAt(0).toUpperCase() + newString.slice(1);
      } else {
        return string.charAt(0).toUpperCase() + string.slice(1);
      }
    } else {
      return null;
    }
  };

  const identity = (a) => a;
  const duration = 500;
  
  function animate(
    {
      easing = identity,
      onProgress = identity,
      onComplete = identity,
      from = {},
      to = {}
    } = {}
  ) {
    const startTime = Date.now();

    function update() {
      let deltaTime = Date.now() - startTime;
      let progress = Math.min(deltaTime / duration, 1);
      let factor = easing(progress);
      let values = {};

      for (let k in from) {
        if (k) {
          values[k] = from[k] + (to[k] - from[k]) * factor;
        }
      }

      onProgress(values);

      if (progress === 1) {
        onComplete(deltaTime);
      } else {
        requestAnimationFrame(update);
      }
    }

    requestAnimationFrame(update);
  }



  function updatePadding(value) {
    return {
      left: value - sidebarMargin.current + defaultPadding.current,
    };
  }

  function expand() {
    const sidebar = document.getElementById("map-sidebar");
    const btnExpand = document.getElementById("btnExpand");
    animate({
      easing: inSine,
      onProgress({ a }) {
        sidebar.style.marginLeft = `${a}px`;
        view.current.padding = updatePadding(a);
      },
      onComplete() {
        btnExpand.removeEventListener("click", expand);
        btnExpand.addEventListener("click", collapse);
      },
      from: {
        a: sidebarMargin.current,
      },
      to: {
        a: 0,
      },
    });
  }

  function collapse() {
    const sidebar = document.getElementById("map-sidebar");
    const btnExpand = document.getElementById("btnExpand");
    animate({
      easing: inSine,
      onProgress({ a }) {
        sidebar.style.marginLeft = `${a}px`;
        view.current.padding = updatePadding(a);
      },
      onComplete() {
        btnExpand.removeEventListener("click", collapse);
        btnExpand.addEventListener("click", expand);
      },
      from: {
        a: 0,
      },
      to: {
        a: sidebarMargin.current,
      },
    });
  }

  return (
    <main className="interactive-map" id="main-content">
      <h1 className="sr-only">Interactive Map</h1>
      <div className="visually-hidden" aria-live="polite">
        {announcement}
      </div>
      <div className="map-top-filter-bar">
        <div className="map-top-filter">
          <label
            id="aria-state-label"
            className="map-input-label"
            htmlFor="select-state"
          >
            State
          </label>
          <Select
            id="select-state"
            aria-label="Select a state. Press the down arrow to expand the option list. Press delete
            to clear the current selection."
            className={
              "react-select-interactive-map-input select-state grayed-out-" +
              extentChange
            }
            placeholder="Select a State"
            name="singleSelect"
            value={selectedState}
            onChange={(value) => setSelectedState(value)}
            options={stateList}
            hideselectedoptions="true"
            isClearable={false}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              placeholder: (baseStyles) => ({
                ...baseStyles,
                color: "#1b1b1b",
              }),
            }}
          />
        </div>
        <div className="map-top-filter">
          <label
            id="aria-tree-label"
            className="map-input-label"
            htmlFor="select-tree"
          >
            Tree Species/Group
          </label>
          <Select
            id="select-tree"
            aria-label="Select a tree species or group. Press the down arrow to expand the option list. Press delete
            to clear the current selection."
            className="react-select-interactive-map-input select-tree"
            placeholder="Select a Tree Species"
            name="singleSelect"
            value={selectedTree}
            onChange={(value) => setSelectedTree(value)}
            options={treeList}
            hideselectedoptions="true"
            isClearable={true}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              placeholder: (baseStyles) => ({
                ...baseStyles,
                color: "#1b1b1b",
              }),
            }}
          />
        </div>
        <div className="map-top-filter">
          <label
            id="aria-pest-label"
            className="map-input-label"
            htmlFor="select-pest"
          >
            Pest Species
          </label>
          <Select
            id="select-pest"
            aria-label="Select a pest species. Press the down arrow to expand the option list. Press delete
            to clear the current selection."
            className="react-select-interactive-map-input select-pest"
            placeholder="Select a Pest Species"
            name="singleSelect"
            value={selectedPest}
            onChange={(value) => setSelectedPest(value)}
            options={pestList}
            hideselectedoptions="true"
            isClearable={true}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              placeholder: (baseStyles) => ({
                ...baseStyles,
                color: "#1b1b1b",
              }),
            }}
          />
        </div>
        <div className="map-accessibility-message">
          An accessible version of the map data is on the{" "}
          <Link className="map-link" to="/pest-information/by-pest">
            Pest Information page
          </Link>
        </div>
      </div>
      <div view={viewState} id={sidebarID.current}>
        <button
          id="btnExpand"
          aria-expanded={sidebarExpanded}
          expanded={sidebarExpanded.toString()}
          title="Expand/Close Sidebar"
          aria-label="Expand/Close Sidebar"
          type="button"
          onClick={toggleSidebar}
        >
          {sidebarExpanded ? (
            <i
              className="fa fa-angle-left btn-expand-icon"
              aria-hidden="true"
            ></i>
          ) : (
            <i
              className="fa fa-angle-right btn-expand-icon"
              aria-hidden="true"
            ></i>
          )}
        </button>

        <div id="map-sidebar-content">
          <div className="usa-accordion">
            <h2 className="usa-accordion__heading">
              <button
                className="usa-accordion__button  usa-accordion-button"
                aria-expanded="false"
                aria-controls="a1"
                aria-label="Open/Close Legend"
                title="Open/Close Legend"
                type="button"
              >
                <i
                  className="fa fa-list-ul sidebar-map-icon"
                  aria-hidden="true"
                />
                <span className="map-sidebar-header">Legend</span>
              </button>
            </h2>
            <div id="a1" className="usa-accordion__content">
              <div id={legendID.current} />
            </div>
            <h2 className="usa-accordion__heading">
              <button
                className="usa-accordion__button  usa-accordion-button"
                aria-expanded="false"
                aria-controls="a2"
                aria-label="Tree Species/Group Information"
                title="Open/Close Tree Information Section"
                type="button"
              >
                <i className="fa fa-tree sidebar-map-icon" aria-hidden="true" />
                <span className="map-sidebar-header">
                  Tree <span className="hide-on-mobile">Species/Group </span>
                  Information
                </span>
              </button>
            </h2>
            <div id="a2" className="usa-accordion__content">
              {selectedTree ? (
                <Fragment>
                  <div
                    className="loading-box"
                    style={{ display: treeLoading ? "flex" : "none" }}
                  >
                    <div className="spinner" />
                  </div>
                  <div
                    className="tree-section"
                    style={{ display: treeLoading ? "none" : "flex" }}
                  >
                    <img
                      src={
                        treePhotoURL
                          ? "/images/" + treePhotoURL
                          : "/images/NoPhoto.png"
                      }
                      width="100%"
                      className="map-info-image"
                      alt={
                        treePhotoURL && selectedTree
                          ? "An example of " + selectedTree.label
                          : "No tree image available"
                      }
                    />
                    <div className="map-photo-credit">{treePhotoCredit}</div>
                    <div className="map-sidebar-info">
                      <h3>
                        {selectedTree.label === "mixed species"
                          ? "Mixed Species"
                          : selectedTree.label}
                      </h3>
                      {treeScientificName && (
                        <div className="scientific-name">
                          {treeScientificName}
                        </div>
                      )}
                      <div className="tree-resources">
                        <h4 className="tree-resource-header first">
                          Tree range source data
                        </h4>
                        <div className="tree-resource">
                          <div className="tree-resource-name">
                            <Link
                              className="usa-link usa-link--external"
                              to="https://www.fs.usda.gov/science-technology/data-tools-products/fhp-mapping-reporting/individual-tree-species-parameter-maps"
                              target="_blank"
                              rel="noreferrer"
                              title="Select to open the Forest Health Protection Individual Tree Ranges souce data"
                            >
                              Forest Health Protection (FHP) Individual Tree
                              Ranges
                            </Link>
                          </div>
                          <p className="tree-resource-description">
                            These data are used within this application to
                            broadly depict current tree species
                            distributions/presence. The individual tree species
                            atlas was developed to support forest pest
                            conditions and risk/hazard assessments. Visit our
                            webpage to learn more about these tree data and the
                            assessments FHP conducts.
                          </p>
                        </div>
                        <h4 className="tree-resource-header">Other datasets</h4>
                        <p className="other-datasets-reason">
                          If you wish to explore other aspects of tree health,
                          check out these other resources
                        </p>
                        {treeAtlasURL && (
                          <div className="tree-resource">
                            <div className="tree-resource-name">
                              <Link
                                className="usa-link usa-link--external"
                                to={treeAtlasURL}
                                target="_blank"
                                rel="noreferrer"
                                title={
                                  "Select to visit the Climate Change Tree Atlas page for " +
                                  selectedTree.label
                                }
                              >
                                Climate Change Tree Atlas
                              </Link>
                            </div>
                            <p className="tree-resource-description">
                              View maps of potential changes in suitable habitat
                              due to predicted climate scenarios through 2100
                              for 125 tree species in the eastern United States.
                            </p>
                          </div>
                        )}
                        <div className="tree-resource">
                          <div className="tree-resource-name">
                            <Link
                              className="usa-link usa-link--external"
                              to="https://apps.fs.usda.gov/forest-atlas/"
                              target="_blank"
                              rel="noreferrer"
                              title="Select to visit the Forest Atlas of the United States website"
                            >
                              Forest Atlas of the United States
                            </Link>
                          </div>
                          <p className="tree-resource-description">
                            Provides a wide range of stories about the value of
                            forests across the United States and the challenges
                            they face.
                          </p>
                        </div>
                        {plantDatabaseURL && (
                          <div className="tree-resource">
                            <div className="tree-resource-name">
                              <Link
                                className="usa-link usa-link--external"
                                to={plantDatabaseURL}
                                target="_blank"
                                rel="noreferrer"
                                title={
                                  "Select to visit the USDA Plant Database page for " +
                                  selectedTree.label
                                }
                              >
                                USDA Plant Database
                              </Link>
                            </div>
                            <p className="tree-resource-description">
                              Access standardized information, such as soil
                              preference, cold hardiness, or shade tolerance,
                              for tree species and other vascular plants
                              residing in the United States and its territories.
                            </p>
                          </div>
                        )}
                        <div className="tree-resource">
                          <div className="tree-resource-name">
                            <Link
                              className="usa-link usa-link--external"
                              to="https://www.fs.usda.gov/science-technology/data-tools-and-products/fhp-mapping-reporting/national-insect-disease-risk-and-hazard-mapping"
                              target="_blank"
                              title="Select to visit the USFS National Insect & Disease Risk and Hazard Mapping website"
                            >
                              National Insect & Disease Risk and Hazard Mapping
                            </Link>
                          </div>
                          <p className="tree-resource-description">
                            Summarizes landscape-level patterns of potential
                            insect and disease activity.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Fragment>
              ) : (
                <div className="map-sidebar-text">Please select a tree</div>
              )}
            </div>
            <h2 className="usa-accordion__heading">
              <button
                className="usa-accordion__button usa-accordion-button"
                aria-expanded="true"
                aria-controls="a3"
                aria-label="Pest Species Information"
                title="Open/Close Pest Information Section"
                type="button"
              >
                <i className="fa fa-bug sidebar-map-icon" aria-hidden="true" />
                <span className="map-sidebar-header">
                  Pest <span className="hide-on-mobile">Species </span>
                  Information
                </span>
              </button>
            </h2>
            <div id="a3" className="usa-accordion__content">
              {pestLoading && (
                <div className="loading-box">
                  <div className="spinner" />
                </div>
              )}
              {!pestLoading &&
                (selectedPest ? (
                  <div className="pest-section">
                    <img
                      src={
                        pestPhotoURL
                          ? "/images/" + pestPhotoURL
                          : "/images/NoPhoto.png"
                      }
                      width="100%"
                      className="map-info-image"
                      alt={
                        pestPhotoURL && selectedPest
                          ? "An example of " + selectedPest.label + " damage"
                          : "No pest image available"
                      }
                    />
                    <div className="map-photo-credit">
                      {pestPhotoCredit || ""}
                    </div>
                    <div className="map-sidebar-info">
                      <h3>
                        {selectedPest && cleanPestName(selectedPest.label)}
                      </h3>
                      <div className="scientific-name">
                        {pestScientificName || ""}
                      </div>
                    </div>
                    <div className="map-sidebar-icons">
                      {pestTableLink && (
                        <a
                          href={pestTableLink}
                          target="_blank"
                          rel="noreferrer"
                          title={
                            "Select to open the Forest Pest Conditions data table for " +
                            selectedPest.label +
                            " in a new window"
                          }
                        >
                          <i className="fa fa-table" aria-hidden="true" />
                        </a>
                      )}
                      {pestFIDLURL ? (
                        <a
                          href={pestFIDLURL}
                          target="_blank"
                          rel="noreferrer"
                          title={
                            "Select to open the Forest Insect & Disease Leaflet for " +
                            selectedPest.label +
                            " in a new window"
                          }
                        >
                          <img
                            src={fidlIcon}
                            className="fidl-icon"
                            alt="Forest Insect & Disease Leaflet icon"
                            aria-hidden="true"
                          />
                        </a>
                      ) : (
                        ""
                      )}
                      <a
                        href="https://www.fs.usda.gov/science-technology/forest-health-protection/publications-reports/forest-health-protection-publications"
                        target="_blank"
                        rel="noreferrer"
                        title="Select to open the Annual Major Forest Insect and Disease Conditions in the United States Report in a new window"
                      >
                        <i className="fa fa-file" aria-hidden="true" />
                      </a>
                    </div>
                    <div className="pest-narrative">
                      {pestNarrative || "No narrative available for this pest."}
                    </div>
                  </div>
                ) : (
                  <div className="map-sidebar-text">Please select a pest</div>
                ))}
            </div>
            <h2 className="usa-accordion__heading">
              <button
                className="usa-accordion__button  usa-accordion-button"
                aria-expanded="false"
                aria-controls="a4"
                title="Open/Close Map Data Context Section"
                type="button"
              >
                <i className="fa fa-info sidebar-map-icon" aria-hidden="true" />
                <span className="map-sidebar-header">Map Data Context</span>
              </button>
            </h2>
            <div id="a4" className="usa-accordion__content">
              <div className="map-sidebar-text">
                Due to the nature of aerial surveys, this data will only provide
                estimates of location, intensity and the resulting trend
                information for pests currently detectable from the air. For
                example, many diseases are not represented in the data because
                these damage agents are not visually detectable from aerial
                surveys. Given these limitations, the data should only be used
                as a partial indicator of insect and disease activity, and
                requires validation on the ground for actual location and causal
                agent.
                <br />
                <br />
                This insect and disease data is available digitally from the
                USDA Forest Service. The cooperators reserve the right to
                correct, update, modify or replace GIS products. Using this data
                for purposes other than those for which it was intended may
                yield inaccurate or misleading results.
              </div>
            </div>
          </div>
        </div>
        <div
          id={shareButtonID.current}
          view={viewState}
          className="esri-widget--button esri-widget"
          aria-label="Copy Share Link"
          role="button"
          tabIndex="0"
          title="Copy Share Link"
          onClick={handleShare}
          ref={shareTarget}
        >
          <i
            className="esri-feature__icon fa fa-share"
            aria-label="Share Link Icon"
            role="img"
          />
          <span className="esri-icon-font-fallback-text">Share</span>
        </div>
        <Overlay
          placement="left"
          show={showShareTooltip}
          target={shareTarget.current}
        >
          {({
            placement: _placement,
            arrowProps: _arrowProps,
            show: _show,
            popper: _popper,
            hasDoneInitialMeasure: _hasDoneInitialMeasure,
            ...props
          }) => (
            <div
              {...props}
              style={{
                position: "absolute",
                backgroundColor: "rgba(255, 255, 255,0.85)",
                marginRight: "10px",
                padding: "2px 10px",
                color: "black",
                borderRadius: 3,
                ...props.style,
              }}
            >
              Link copied
            </div>
          )}
        </Overlay>
        <div id={yearSwitcherID.current} view={viewState}>
          <div id="year-switcher-select">
            {yearList && (
              <Select
                className="react-select info year-select"
                classNamePrefix="react-select"
                placeholder="Select Year"
                aria-label="Select a year. Press the down arrow to expand the option list. Press delete
              to clear the current selection."
                name="singleSelect"
                value={year}
                onChange={(value) => setYear(value)}
                options={yearList}
                autosize={false}
                styles={{
                  placeholder: (baseStyles) => ({
                    ...baseStyles,
                    color: "#1b1b1b",
                  }),
                }}
              />
            )}
          </div>
          {yearList && (
            <div id="time-controls">
              <div
                className="esri-widget--button esri-widget esri-interactive"
                role="button"
                tabIndex="0"
                onClick={timeSliderBackward}
                onKeyDown={(e) => {
                  if (e.key === "Enter") timeSliderBackward();
                }}
              >
                <span
                  className="fa fa-step-backward"
                  role="img"
                  aria-label="Previous Year"
                ></span>
              </div>
              {!play && (
                <div
                  className="esri-widget--button esri-widget esri-interactive"
                  role="button"
                  tabIndex="0"
                  onClick={timeSliderPlay}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") timeSliderPlay();
                  }}
                >
                  <span
                    role="img"
                    className="fa fa-play"
                    aria-label="Play"
                  ></span>
                </div>
              )}
              {play && (
                <div
                  className="esri-widget--button esri-widget esri-interactive"
                  role="button"
                  tabIndex="0"
                  onClick={timeSliderStop}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") timeSliderStop();
                  }}
                >
                  <span
                    role="img"
                    className="fa fa-pause"
                    aria-label="Pause"
                  ></span>
                </div>
              )}
              <div
                className="esri-widget--button esri-widget esri-interactive last-button"
                role="button"
                tabIndex="0"
                onClick={timeSliderForward}
                onKeyDown={(e) => {
                  if (e.key === "Enter") timeSliderForward();
                }}
              >
                <span
                  role="img"
                  className="fa fa-step-forward"
                  aria-label="Next Year"
                ></span>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mapDiv" ref={mapDiv} aria-label="Interactive Map" />
    </main>
  );
}

export default InteractiveMap;
